import type { Table as TanstackTable } from '@tanstack/react-table';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useIntl } from '@/core/i18n/use-intl';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui';

interface TablePaginationProps<T extends object> {
  table: TanstackTable<T>;
  /** Page size options to show in selector */
  pageSizeOptions?: number[];
  /** Whether to show page size selector */
  showPageSizeSelector?: boolean;
}

/**
 * Pagination component for tables matching the prototype design.
 * Shows "Zeige X bis Y von Z Ergebnissen" format.
 */
export function TablePagination<T extends object>({
  table,
  pageSizeOptions = [10, 20, 30, 50],
  showPageSizeSelector = true,
}: TablePaginationProps<T>) {
  const { t } = useIntl();

  const pageIndex = table.getState().pagination.pageIndex;
  const pageSize = table.getState().pagination.pageSize;
  const totalPages = table.getPageCount();
  const totalItems = table.getRowCount();

  // Calculate "showing X to Y of Z"
  const startItem = totalItems === 0 ? 0 : pageIndex * pageSize + 1;
  const endItem = Math.min((pageIndex + 1) * pageSize, totalItems);

  const canGoToPrevious = pageIndex > 0;
  const canGoToNext = pageIndex < totalPages - 1;

  return (
    <>
      <div className="flex items-center gap-4">
        <div className="text-sm text-muted-foreground">
          {t('pagination.showing')} <span className="font-medium text-foreground">{startItem}</span>{' '}
          {t('pagination.to')} <span className="font-medium text-foreground">{endItem}</span>{' '}
          {t('common.of')} <span className="font-medium text-foreground">{totalItems}</span>{' '}
          {t('pagination.results')}
        </div>

        {showPageSizeSelector && (
          <div className="hidden md:flex items-center gap-2">
            <span className="text-sm text-muted-foreground">{t('pagination.rowsPerPage')}</span>
            <Select
              value={`${pageSize}`}
              onValueChange={(value) => {
                table.setPageSize(Number(value));
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue placeholder={pageSize} />
              </SelectTrigger>
              <SelectContent side="top">
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={`${size}`}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => table.previousPage()}
          disabled={!canGoToPrevious}
          className="p-2 border border-border rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft size={16} />
        </button>
        <button
          type="button"
          onClick={() => table.nextPage()}
          disabled={!canGoToNext}
          className="p-2 border border-border rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </>
  );
}
